import React from "react";
import { Link } from "react-router-dom";
import { Music2, Laugh, Film, PersonStanding, Palette, Shirt, Mic2, ChefHat, Sparkles, Check, ArrowRight } from "lucide-react";

const CATEGORIES = [
  {
    key: "music",
    emoji: "🎵",
    icon: Music2,
    title: "Musique",
    lead: "La musique est au cœur de Voamada.",
    items: ["Artistes indépendants et confirmés", "Clips vidéo et performances live", "Albums, EP et singles", "DJ, beatmakers et producteurs"],
    tag: "Les artistes peuvent publier leurs projets, gagner en visibilité et se faire repérer.",
    color: "from-brand-red/20 to-brand-orange/10",
  },
  {
    key: "humor",
    emoji: "😂",
    icon: Laugh,
    title: "Humour & Comédie",
    lead: "Parce que le talent, c'est aussi faire rire.",
    items: ["Humoristes et stand-up", "Sketchs et vidéos humoristiques", "Créateurs de contenus (TikTok, réseaux sociaux)", "Parodies et formats courts"],
    tag: "Une catégorie idéale pour du contenu viral et divertissant.",
    color: "from-brand-yellow/20 to-brand-orange/10",
  },
  {
    key: "cinema",
    emoji: "🎬",
    icon: Film,
    title: "Cinéma & Vidéo",
    lead: "Une vitrine pour les créateurs d'images.",
    items: ["Acteurs et actrices", "Réalisateurs", "Courts métrages et web séries", "YouTubers et créateurs vidéo"],
    tag: "Voamada permet de valoriser les productions locales et indépendantes.",
    color: "from-purple-500/20 to-brand-red/10",
  },
  {
    key: "dance",
    emoji: "💃",
    icon: PersonStanding,
    title: "Danse & Performance",
    lead: "Le mouvement et l'énergie à l'honneur.",
    items: ["Danseurs et groupes", "Chorégraphies", "Battles", "Danses traditionnelles et modernes"],
    tag: "Une catégorie vivante qui reflète la richesse culturelle.",
    color: "from-[#0B8B47]/20 to-brand-yellow/10",
  },
  {
    key: "art",
    emoji: "🎨",
    icon: Palette,
    title: "Art visuel",
    lead: "L'expression artistique sous toutes ses formes.",
    items: ["Peintres", "Dessinateurs", "Photographes", "Graphistes"],
    tag: "Possibilité de présenter et vendre ses œuvres.",
    color: "from-pink-500/20 to-brand-red/10",
  },
  {
    key: "fashion",
    emoji: "👗",
    icon: Shirt,
    title: "Mode & Création",
    lead: "La créativité au service du style.",
    items: ["Stylistes", "Marques africaines", "Modèles", "Défilés et shootings"],
    tag: "Une vitrine pour la mode africaine et malgache.",
    color: "from-rose-500/20 to-amber-500/10",
  },
  {
    key: "food",
    emoji: "🍲",
    icon: ChefHat,
    title: "Cuisine & recettes",
    lead: "La richesse des saveurs gasy et africaines.",
    items: ["Chefs et cuisiniers", "Recettes malgaches traditionnelles", "Street food et pâtisserie", "Tutoriels cuisine et vlog food"],
    tag: "Une catégorie conviviale pour partager l'art culinaire du continent.",
    color: "from-amber-500/20 to-brand-red/10",
  },
  {
    key: "other",
    emoji: "🎤",
    icon: Mic2,
    title: "Autres talents",
    lead: "Parce que le talent ne se limite pas à une catégorie.",
    items: ["Slam et poésie", "Conteurs", "Magiciens", "Animateurs"],
    tag: "Une ouverture à tous les talents émergents.",
    color: "from-sky-500/20 to-[#0B8B47]/10",
  },
];

const PERKS = [
  "Publier gratuitement son profil",
  "Gagner en visibilité",
  "Être contacté pour des opportunités",
  "Développer sa carrière",
  "Rejoindre une communauté artistique",
];

export const AboutPage = () => {
  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="about-page">
      {/* Logo Hero Animation */}
      <section
        className="relative flex flex-col items-center justify-center text-center mb-14 pt-6 pb-10 overflow-hidden"
        data-testid="about-logo-hero"
      >
        {/* Radial backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 45%, rgba(234,179,8,0.12), rgba(227,27,35,0.06) 45%, transparent 75%)",
          }}
        />

        {/* Logo with animation */}
        <div className="relative logo-hero-float">
          <div className="logo-hero-anim logo-hero-glow relative">
            <img
              src="/assets/icon-512.png"
              alt="Voamada — logo"
              width={220}
              height={220}
              className="relative z-10 w-[160px] sm:w-[200px] md:w-[240px] h-auto rounded-[28%] select-none"
              draggable={false}
              data-testid="about-logo-image"
            />
          </div>
        </div>

        {/* Tagline below */}
        <p
          className="relative mt-8 inline-flex items-center gap-2 tag-pill bg-brand-red/10 border-brand-red/40 text-brand-red"
          style={{ animation: "fade-up 0.7s ease-out 1.1s both" }}
        >
          <Sparkles size={12} /> Voamada — la graine qui fait pousser les talents
        </p>
        <h1
          className="relative font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-5 text-balance max-w-3xl"
          style={{ animation: "fade-up 0.7s ease-out 1.25s both" }}
        >
          Bienvenue sur{" "}
          <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
            Voamada
          </span>
          .
        </h1>
        <p
          className="relative text-lg md:text-xl text-white/70 max-w-2xl mt-5 text-balance"
          style={{ animation: "fade-up 0.7s ease-out 1.4s both" }}
        >
          La plateforme dédiée aux talents malgaches et africains.
        </p>
      </section>

      {/* Intro */}
      <div className="text-center mb-16">
        <p className="text-white/60 max-w-3xl mx-auto">
          Notre objectif est simple : <strong className="text-white">mettre en lumière les artistes</strong>,
          leur donner de la visibilité et leur permettre de développer leur carrière.
        </p>
        <p className="text-white/60 max-w-3xl mx-auto mt-3">
          Que vous soyez artiste, créateur ou passionné, Voamada est l'endroit idéal pour
          <strong className="text-brand-orange"> découvrir, partager et connecter</strong> les talents.
        </p>
      </div>

      {/* Categories */}
      <div className="grid md:grid-cols-2 gap-6 mb-20 stagger">
        {CATEGORIES.map((c) => (
          <div
            key={c.key}
            className={`relative rounded-2xl border border-white/10 overflow-hidden bg-gradient-to-br ${c.color} p-7`}
            data-testid={`category-${c.key}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">{c.emoji}</span>
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">{c.title}</h2>
            </div>
            <p className="text-white/80 mb-4 italic">{c.lead}</p>
            <ul className="space-y-2 mb-5">
              {c.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-white/80">
                  <Check size={14} className="text-brand-red mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <div className="pt-4 border-t border-white/10">
              <p className="text-sm text-white/60">
                <span className="text-brand-orange font-semibold">→</span> {c.tag}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Why join */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 md:p-12 mb-16 text-center">
        <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-yellow/10 border-brand-yellow/40 text-brand-yellow">
          🚀 Pourquoi rejoindre Voamada ?
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-black mb-8">Une plateforme faite pour vous.</h2>
        <ul className="grid md:grid-cols-2 gap-3 max-w-2xl mx-auto text-left mb-8">
          {PERKS.map((p) => (
            <li key={p} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
              <span className="w-8 h-8 rounded-full bg-brand-red/15 text-brand-red flex items-center justify-center text-sm">✔</span>
              <span className="text-sm">{p}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="about-cta-register">
            Créer mon compte gratuit <ArrowRight size={14} />
          </Link>
          <Link to="/explore" className="btn-ghost inline-flex items-center gap-2" data-testid="about-cta-explore">
            Découvrir les talents
          </Link>
        </div>
      </div>

      {/* Vision */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-red/20 via-brand-orange/15 to-brand-yellow/10 border border-white/10 p-8 md:p-12 text-center">
        <p className="text-[10px] tracking-[0.25em] uppercase text-brand-yellow font-bold mb-4">🌟 Notre vision</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-6 text-balance">
          Créer une plateforme unique qui rassemble les talents africains et malgaches.
        </h2>
        <p className="text-white/80 text-lg max-w-2xl mx-auto">
          <strong className="text-brand-red">Voamada</strong>, c'est bien plus qu'un site :
          c'est un <strong>tremplin</strong> pour les artistes.
        </p>
      </div>
    </div>
  );
};

export default AboutPage;
